import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ChevronDown, ArrowRight } from 'lucide-react';

interface FaqItem {
  q: string;
  a: string;
  bullets?: string[];
}

interface FaqCategory {
  title: string;
  items: FaqItem[];
}

const categories: FaqCategory[] = [
  {
    title: 'General',
    items: [
      {
        q: 'What is Azura Mobility?',
        a: 'Azura Mobility is a Nigerian-founded electric mobility company building a fully integrated network of electric vehicles, Battery-as-a-Service (BaaS) battery-swap stations, ride-hailing services, and long-term local EV manufacturing across Africa. We are tokenizing real-world fleet assets through the AZT platform token.',
      },
      {
        q: 'Why was Azura founded?',
        a: "Africa is at a defining crossroads. Fuel prices have surged 4–6x after subsidy removal, commercial motorcycle riders spend 30–50% of income on petrol, and the continent imports nearly all its 25 million motorcycles. Azura exists to make clean, affordable transportation accessible while building Africa's first local EV manufacturer.",
      },
      {
        q: 'Where is Azura launching?',
        a: "Ibadan, Nigeria: Nigeria's third-largest city with ~3.6 million people. Unlike Lagos, Ibadan has no Okada (commercial motorcycle) ban, offers proven cost and earnings per bike, and has no major electric mobility competitor with primary focus there.",
      },
      {
        q: 'What makes Azura different from competitors?',
        a: "Azura is the only operator combining an owned battery-swap network, an integrated ride-hailing platform, a community token (AZT), and a phased path to full local manufacturing. We are also Nigerian-headquartered, not foreign-owned, which matters for government partnerships and community trust.",
      },
    ],
  },
  {
    title: 'AZT Token',
    items: [
      {
        q: 'What is AZT?',
        a: 'AZT (Azura Token) is the platform token of the Azura network. It is backed by real-world assets, EVs on the road, battery swap stations, and operating revenue.',
      },
      {
        q: 'What is the total supply of AZT?',
        a: '1,000,000,000 AZT. Fixed supply. No inflation.',
      },
      {
        q: 'Is AZT a security token?',
        a: "No. AZT is a platform token designed for network participation, raising funds, and long-term alignment between supporters and Azura's growth. It does not represent equity or guarantee investment returns.",
      },
      {
        q: 'Does AZT have a burn mechanism?',
        a: 'Yes. 10% of operational revenue is allocated to quarterly AZT token burns, carried out on the blockchain, reducing the number of tokens in circulation over time.',
      },
      {
        q: 'How is AZT allocated?',
        a: 'The 1B fixed supply is split across nine allocation categories:',
        bullets: [
          'Strategic Round: 10% (100M AZT)',
          'Presale: 10% (100M AZT)',
          'Public Launch: 10% (100M AZT)',
          'Network Development: 15% (150M AZT)',
          'Treasury Reserve: 15% (150M AZT)',
          'Expansion Fund: 15% (150M AZT)',
          'Team & Advisors: 10% (100M AZT)',
          'Research & Development: 10% (100M AZT)',
          'Liquidity Reserve: 5% (50M AZT)',
        ],
      },
    ],
  },
  {
    title: 'Strategic Round & Fundraising',
    items: [
      {
        q: 'What is the Strategic Round?',
        a: "The Strategic Round is Azura's first token sale, structured as 25,000 units at $20 per unit. Each unit contains 4,000 AZT tokens ($0.005 per AZT). It raises $500,000 to fund Phase 1 launch.",
      },
      {
        q: 'How much does one unit cost?',
        a: '$20 per unit (4,000 AZT). Payment accepted in stablecoins or the equivalent in regular currency.',
      },
      {
        q: 'What is the minimum purchase?',
        a: 'One unit ($20). This makes participation accessible to retail supporters.',
      },
      {
        q: 'When does the Strategic Round end?',
        a: 'The Strategic Round runs for a limited time or until all 25,000 units are sold, whichever comes first. Check the website or Telegram for real-time unit availability.',
      },
      {
        q: 'What happens after the Strategic Round?',
        a: 'The Presale follows at $0.010 per AZT ($1.0M raise), then the Public Launch at $0.020 per AZT ($2.0M raise).',
      },
      {
        q: 'How will the $500,000 be used?',
        a: 'Phase 1 upfront costs: 500 electric motorcycles, 17 battery swap stations, technology platform development, and operational launch in Ibadan. Total Phase 1 costs are ~$1.38M, with the remaining ~$880K covered by Presale proceeds.',
      },
    ],
  },
  {
    title: 'Real-World Asset (RWA) Backing',
    items: [
      {
        q: 'What backs the AZT token?',
        a: 'AZT is backed by real assets and revenue: electric motorcycles on the road, battery swap stations, fleet payment income ($6/day per bike), BaaS swap revenue, and vehicle sales. This is not a speculative meme token. It is tied to real, income-generating transport assets.',
      },
      {
        q: 'How does the RWA model work?',
        a: "Every AZT token is linked to Azura's operations. As we put more vehicles and stations on the road, operational revenue grows. A portion of that revenue is used for token burns and network development, directly connecting token value to real-world fleet performance.",
      },
    ],
  },
  {
    title: 'Business Model & Revenue',
    items: [
      {
        q: 'How does Azura make money?',
        a: 'Through five primary revenue streams:',
        bullets: [
          'Fleet Operations: $6/day payment per bike',
          'Battery-as-a-Service (BaaS): $1.00/swap (fleet) / $1.20/swap (walk-in)',
          'Electric Vehicle Sales: $2,040 average sale price per unit',
          'Electric Ride-Hailing: Phase 2 (2028–2030)',
          'Carbon Finance: carbon credits via Gold Standard / Verra at scale',
        ],
      },
      {
        q: 'What are the per-bike economics?',
        a: "Each bike on the road generates $1,872 in annual fleet revenue (base case, 312 operating days, 15% default buffer). At 500 bikes, that's $796K/year. At 500,000 bikes, fleet revenue alone reaches $795.6M/year.",
      },
      {
        q: 'What is Battery-as-a-Service (BaaS)?',
        a: 'Instead of riders waiting 4–8 hours to charge, they swap depleted batteries for fully charged ones at Azura stations in under two minutes. We own the stations and batteries; riders pay per swap. This removes the need to rely on the power grid and eliminates charging downtime.',
      },
    ],
  },
  {
    title: 'Technology & Operations',
    items: [
      {
        q: 'What technology does Azura use?',
        a: 'Azura operates its own technology, including:',
        bullets: [
          'Azura Mobile App: fleet access, wallet, BaaS station finder, ride-hailing',
          'Fleet Management System: real-time tracking, revenue monitoring, maintenance scheduling',
          'Battery Management System: individual battery tracking, charge monitoring, swap recording, predictive maintenance',
          'Ride-Hailing Platform: Phase 2 launch (2028)',
          'Blockchain Layer: AZT token management, burn transparency, network participation',
        ],
      },
      {
        q: 'How do riders pay for swaps?',
        a: 'Through the Azura mobile app with integrated digital wallet.',
      },
      {
        q: 'How do you mitigate rider payment default risk?',
        a: 'Risk is mitigated through mandatory guarantor support, rider verification, digital identity validation, real-time vehicle tracking, daily collection, and insurance coverage. Base case models 15% default.',
      },
    ],
  },
  {
    title: 'Manufacturing Roadmap',
    items: [
      {
        q: 'Is Azura just importing EVs?',
        a: 'No. While Phase 1 uses imported vehicles, Azura has a deliberate four-stage manufacturing roadmap:',
        bullets: [
          'Roll Out & Operate (2026–2032): fleet and battery-swap stations',
          'Local Assembly (2033–2037): local assembly of knocked-down kits in Nigeria',
          'Partial manufacturing (2038–2041): own drivetrain and battery technology',
          'Full manufacturing (2042–2045): complete design, engineering, and manufacturing ownership',
        ],
      },
      {
        q: 'Why not start manufacturing immediately?',
        a: 'Each stage self-funds the next. This is the proven industrial approach used by BYD, Hyundai, and Kia. Attempting full manufacturing from launch would require hundreds of millions in funding and unacceptable risk. Phased execution manages risk incrementally.',
      },
      {
        q: 'When will the first Azura-branded vehicle be produced?',
        a: '2033: the first locally assembled Azura vehicle from knocked-down kits.',
      },
    ],
  },
  {
    title: 'Climate & Impact',
    items: [
      {
        q: 'How does Azura reduce emissions?',
        a: "Every electric motorcycle on the road replaces a petrol-powered one, reducing CO2 and urban air pollution. At 100,000 vehicles, Azura's fleet generates measurable annual emissions reductions eligible for carbon credit registration.",
      },
      {
        q: 'Which UN Sustainable Development Goals does Azura align with?',
        a: 'SDG 7 (Clean Energy), SDG 8 (Decent Work), SDG 9 (Industry & Innovation), SDG 11 (Sustainable Cities), and SDG 13 (Climate Action).',
      },
    ],
  },
  {
    title: 'Risks & Security',
    items: [
      {
        q: 'What are the main risks?',
        a: 'Key risks include regulatory changes, slower-than-expected market adoption, competition from well-funded competitors, funding availability, rider default, execution complexity, technology failures, and broader economic instability (Naira depreciation, inflation).',
      },
      {
        q: 'How does Azura mitigate these risks?',
        a: 'Through phased execution, proven per-bike economics, diversified revenue streams, insurance coverage, proactive government engagement, disciplined use of funding, and continuous operational improvement.',
      },
      {
        q: 'Is my investment safe?',
        a: 'All investments carry risk. AZT is a platform token tied to an early-stage operating company. Participants should only commit money they can afford to lose and should read the full whitepaper before purchasing.',
      },
    ],
  },
  {
    title: 'Getting Involved',
    items: [
      {
        q: 'How do I buy AZT in the Strategic Round?',
        a: 'Visit our Strategic Round page, connect your wallet or set up payment using regular currency, and purchase units at $20 each. Detailed instructions are provided after identity verification.',
      },
      {
        q: 'How can I join the community?',
        a: 'Join our Telegram community and follow us on X (Twitter) for real-time updates, AMAs, and announcements.',
      },
      {
        q: 'How can I partner with Azura?',
        a: 'For partnership inquiries, including battery suppliers, fleet technology partners, state governments, logistics partners, and ride-hailing collaborators, contact us through the website or Telegram.',
      },
      {
        q: 'Where can I read the full whitepaper?',
        a: 'The Azura Mobility Whitepaper v2.0 is available for download on our website.',
      },
    ],
  },
];

const FAQ: React.FC = () => {
  const [openKey, setOpenKey] = useState<string | null>('0-0');

  const toggle = (key: string) => setOpenKey(prev => (prev === key ? null : key));

  return (
    <div className="min-h-screen bg-bg-cream text-bg-dark antialiased">

      {/* ══ HERO ══ */}
      <section className="relative bg-bg-dark overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute -top-20 -right-20 w-72 h-72 rounded-full bg-accent-amber/10 blur-3xl" />
          <div className="absolute -bottom-20 -left-20 w-80 h-80 rounded-full bg-white/5 blur-3xl" />
          <div className="absolute inset-0" style={{ background: 'linear-gradient(135deg, rgba(255,184,0,0.07) 0%, transparent 50%, rgba(255,255,255,0.05) 100%)' }} />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-6 py-28 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7">
            <div className="inline-flex items-center gap-2 rounded-full bg-white/10 backdrop-blur-md border border-white/10 px-4 py-2 mb-6">
              <Link to="/" className="text-white/60 hover:text-accent-amber transition text-xs font-semibold">Home</Link>
              <span className="text-white/70">/</span>
              <span className="text-accent-amber text-xs font-semibold">FAQ</span>
            </div>

            <h1 className="text-5xl md:text-6xl font-figtree font-black text-white leading-tight max-w-3xl mb-6">
              Frequently Asked <span className="text-accent-amber">Questions</span>
            </h1>

            <p className="max-w-2xl text-white/70 text-base leading-8">
              Answers on the AZT platform token, the Strategic Round, per-bike economics, and Azura's phased path to Africa's first local EV manufacturer.
            </p>

            <p className="mt-4 text-xs text-white/70 uppercase tracking-widest">
              AZT Platform Token · Whitepaper v2.0 · Ibadan, Nigeria · 2026
            </p>
          </div>

        </div>
      </section>

      {/* ══ CTA 2 ══ */}
      <section className="py-10 px-6 bg-white border-b border-border">
        <div className="max-w-3xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4 bg-bg-cream border border-border rounded-2xl px-6 py-5">
          <p className="font-figtree font-bold text-sm text-bg-dark text-center sm:text-left">
            Got your answers? <span className="text-accent-amber">The Strategic Round opens October 15.</span>
          </p>
          <Link to="/invest"
            className="inline-flex items-center gap-2 bg-bg-dark text-white px-5 py-2.5 rounded-full font-figtree font-bold text-xs uppercase tracking-wider hover:bg-accent-amber hover:text-bg-dark transition-all shrink-0">
            Invest Now <ArrowRight size={13} />
          </Link>
        </div>
      </section>

      {/* ══ CONTENT ══ */}
      <section className="py-20 px-6">
        <div className="max-w-3xl mx-auto">
          {categories.map((cat, ci) => (
            <React.Fragment key={cat.title}>
            {ci === 3 && (
              <div className="mb-14 grid grid-cols-2 gap-4">
                <div className="relative rounded-2xl overflow-hidden aspect-[4/3] border border-border shadow-card">
                  <img src="/images/impact1.jpg" alt="Azura in the field" className="w-full h-full object-cover" />
                </div>
                <div className="relative rounded-2xl overflow-hidden aspect-[4/3] border border-border shadow-card">
                  <img src="/images/bg2.jpg" alt="Azura fleet" className="w-full h-full object-cover" />
                </div>
              </div>
            )}
            <div className="mb-14 last:mb-0">
              <div className="flex items-center gap-3 mb-5">
                <div className="flex items-center -space-x-1">
                  <div className="w-3 h-3 rounded-full bg-accent-amber" />
                  <div className="w-3 h-3 rounded-full bg-bg-dark border-l border-bg-cream" />
                </div>
                <h2 className="text-xs font-bold uppercase tracking-[0.2em] text-bg-dark/50">{cat.title}</h2>
              </div>

              <div className="space-y-3">
                {cat.items.map((item, ii) => {
                  const key = `${ci}-${ii}`;
                  const open = openKey === key;
                  return (
                    <motion.div
                      key={item.q}
                      initial={{ opacity: 0, y: 16 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true }}
                      transition={{ duration: 0.45, delay: Math.min(ii, 4) * 0.04, ease: [0.16, 1, 0.3, 1] }}
                      className="bg-white border border-border rounded-2xl shadow-card overflow-hidden">
                      <button
                        onClick={() => toggle(key)}
                        className="w-full flex items-center justify-between gap-4 text-left px-6 py-5">
                        <span className="font-figtree font-bold text-sm md:text-base text-bg-dark">{item.q}</span>
                        <ChevronDown size={16}
                          className={`shrink-0 text-accent-amber transition-transform duration-300 ${open ? 'rotate-180' : ''}`} />
                      </button>
                      <div className={`grid transition-all duration-300 ease-out ${open ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]'}`}>
                        <div className="overflow-hidden">
                          <div className="px-6 pb-6 border-t border-border pt-4">
                            <p className="font-exo text-sm text-neutral-600 leading-relaxed">{item.a}</p>
                            {item.bullets && (
                              <ul className="mt-3 space-y-1.5">
                                {item.bullets.map(b => (
                                  <li key={b} className="flex items-start gap-2 font-exo text-sm text-neutral-600 leading-relaxed">
                                    <span className="mt-2 w-1 h-1 rounded-full bg-accent-amber shrink-0" />
                                    {b}
                                  </li>
                                ))}
                              </ul>
                            )}
                          </div>
                        </div>
                      </div>
                    </motion.div>
                  );
                })}
              </div>
            </div>
            </React.Fragment>
          ))}

          <div className="pt-6 border-t border-border text-center">
            <p className="text-xs text-neutral-400 uppercase tracking-widest mb-2">
              Azura Mobility · AZT Platform Token · Ibadan, Nigeria · 2026
            </p>
            <p className="text-xs font-bold text-accent-amber uppercase tracking-widest mb-6">
              Driving Africa Forward. Electrically.
            </p>
            <div className="flex flex-wrap justify-center gap-3">
              <a href="mailto:info@azuramobility.com"
                className="inline-flex items-center gap-2 bg-bg-dark text-white px-6 py-3 rounded-full font-figtree font-bold text-xs uppercase tracking-wider hover:bg-accent-amber hover:text-bg-dark transition-all shadow-capsule">
                info@azuramobility.com
              </a>
              <Link to="/opportunity"
                className="inline-flex items-center gap-2 border border-bg-dark/25 bg-white text-bg-dark px-6 py-3 rounded-full font-figtree font-bold text-xs uppercase tracking-wider hover:border-bg-dark transition-all">
                The Opportunity <ArrowRight size={13} />
              </Link>
            </div>
          </div>
        </div>
      </section>

    </div>
  );
};

export default FAQ;
